/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import { SidebarItem } from '@backstage/core-components';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import { useTeamAdminAccess } from '../teamAdmin/useTeamAdminAccess';
import { useViewAs } from '../viewAs/ViewAsContext';

/**
 * Sidebar entry for the Team Admin page, shown only to users the backend gate
 * allows (team-admin-reader /access probe), which also returns the allowed
 * groups + users.
 *
 * Under the "View as" lens the item FAITHFULLY reflects what the VIEWED user
 * sees: it shows iff the viewed user is allowed -- even when the signed-in
 * admin isn't -- so an over-privileged user is visible for diagnosis. This is
 * safe because the lens is read-only: visibility is UX only, and the Team Admin
 * page + every backend endpoint still authorize the REAL signed-in user, so a
 * click can never act with access the admin lacks.
 *
 * (Computing the viewed user's access needs the gate's allowed groups/users,
 * which /access only returns to allowed callers; in this deployment every
 * lens-admin is also a team-admin, so the config is available. If it weren't,
 * the item falls back to hidden.)
 */
export const TeamAdminSidebarItem = () => {
  const { allowed, groups, users } = useTeamAdminAccess();
  const { viewingAs, viewAsLogin, viewAsGroups } = useViewAs();

  const visible = viewingAs
    ? (viewAsLogin !== undefined && users.includes(viewAsLogin)) ||
      viewAsGroups.some(g => groups.includes(g))
    : allowed;

  if (!visible) return null;

  return (
    <SidebarItem icon={SupervisorAccountIcon} to="/team-admin" text="Team Admin" />
  );
};
